// A proposal's urgency (Poulpe-674): a badge where it shows, and a person's
// control to mark it urgent, or normal again, with a reason
// (set_proposal_urgency), from the revision the page shows. Urgency orders
// the landings that may start, and the checks that wait on a runner
// (Poulpe-680), and nothing else: the words say so.
import { useId, useState, type FormEvent } from 'react'
import type { Proposal, Urgency } from '../api/types'
import { URGENCY_REASON_MAX } from '../api/types'
import { useMounted } from '../components/layout'
import { needsServer } from '../components/needsServer'
import { toast } from '../components/toasts'
import { useBoardState, useStore } from '../store/context'
import { actorName } from '../vocab'
import { short } from './words'

/** What an urgent proposal gets, and what it does not. */
export const URGENT_MEANS =
  'An urgent proposal lands before normal ones, a normal picked batch included, but never more than two turns in a row while normal work waits. Its checks go before normal ones waiting on the same runner, within the same allowance. A landing or a check that runs is never stopped.'

/**
 * Who marked it urgent, and why: the badge's title. The API has no
 * directory of other members, so a person is "you" or "a person".
 */
export function urgencyTitle(proposal: Proposal, me?: string): string {
  const { reason, by } = proposal.urgency
  if (!reason || !by) return URGENT_MEANS
  const who = by.kind === 'person' ? (by.member === me ? 'you' : 'a person') : actorName(by)
  return `Urgent, marked by ${who}: ${reason}\n${URGENT_MEANS}`
}

/** "Urgent", for an urgent proposal; nothing for a normal one. */
export function UrgencyBadge({ proposal }: { proposal: Proposal }) {
  const { me } = useBoardState()
  if (proposal.urgency.level !== 'urgent') return null
  return <span className="urgency-badge" title={urgencyTitle(proposal, me?.member?.id)}>Urgent</span>
}

/** What urgency does for the proposal where it stands now. */
export function urgencyEffect(proposal: Proposal): string | null {
  if (proposal.urgency.level !== 'urgent' || proposal.state !== 'open') return null
  switch (proposal.step) {
    case 'head':
    case 'check':
    case 'review':
      return 'Urgent: it will take its turn to land before normal work. While its check waits on its runner, it goes before normal checks there.'
    case 'queued':
      return proposal.held_by
        ? 'Urgent, but held for a person: urgency does not start held work.'
        : 'Urgent: it takes its turn to land before normal work, at most two turns in a row while normal work waits.'
    case 'landing':
      // A picked batch waiting for its turn, or a deferred landing once
      // approved (Poulpe-666), takes its most urgent member's urgency.
      return 'Urgent: while its landing waits for its turn, it goes before normal work. A landing that runs, or waits for its approval, is not hurried.'
    default:
      return null
  }
}

/** A person marks the proposal urgent, or normal again, with a reason for its issues' records. */
export function UrgencyControl({ proposal }: { proposal: Proposal }) {
  const [open, setOpen] = useState(false)
  if (proposal.state !== 'open') return null
  const to: Urgency = proposal.urgency.level === 'urgent' ? 'normal' : 'urgent'
  if (!open) {
    return <button type="button" className="button button-small button-quiet urgency-toggle" onClick={() => setOpen(true)}>
      {to === 'urgent' ? 'Mark urgent…' : 'Mark normal…'}
    </button>
  }
  return <UrgencyForm key={`${proposal.id}-${proposal.urgency.revision}`} proposal={proposal} to={to} onDone={() => setOpen(false)} />
}

function UrgencyForm({ proposal, to, onDone }: { proposal: Proposal; to: Urgency; onDone: () => void }) {
  const store = useStore()
  const { offline } = useBoardState()
  const mounted = useMounted()
  const reasonId = useId()
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const send = async (event: FormEvent) => {
    event.preventDefault()
    if (busy || !reason.trim()) return
    setBusy(true)
    const outcome = await store.setProposalUrgency(proposal, to, reason.trim())
    if (outcome.ok) {
      toast(to === 'urgent' ? `Marked proposal ${short(proposal.head)} urgent.` : `Marked proposal ${short(proposal.head)} normal.`)
      // The answer's new revision may have remounted this form already
      // (its key): the control above closes it either way.
      onDone()
    } else toast(outcome.message, 'error')
    if (mounted.current) setBusy(false)
  }
  return <form className="urgency-form" onSubmit={(event) => void send(event)}>
    {to === 'urgent' && <p className="urgency-means">{URGENT_MEANS}</p>}
    <label className="visually-hidden" htmlFor={reasonId}>{to === 'urgent' ? 'Why it is urgent' : 'Why it is normal again'}</label>
    <textarea id={reasonId} value={reason} onChange={(event) => setReason(event.target.value)} rows={2} maxLength={URGENCY_REASON_MAX}
      placeholder={to === 'urgent' ? 'Why must it land first? Its issues’ records keep this.' : 'Why is it normal again?'} autoFocus disabled={busy} />
    <div className="urgency-form-actions">
      <button type="button" className="button button-small button-quiet" onClick={onDone} disabled={busy}>Cancel</button>
      <button type="submit" className="button button-small button-primary" disabled={busy || offline || !reason.trim()} {...needsServer(offline)}>
        {to === 'urgent' ? 'Mark urgent' : 'Mark normal'}
      </button>
    </div>
  </form>
}
